import { useState, useEffect } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion } from '@mui/material'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import difference from 'lodash/difference'
import { array, func, string, object } from 'prop-types'
import { t } from '@lingui/core/macro'

function checkForMatches(newTargets, baseClassifications) {
  const matches = newTargets.reduce((memo, item) => {
    if (!difference(baseClassifications, item?.classifications_names).length) {
      // only valid MTU options are the ones that are ones that contain all the same classifications as the selected target (adobe version)
      memo.push(item)
    }
    return memo
  }, [])
  return matches
}

export const EditMTU = ({
  multiTargets = [],
  realms = [],
  adobeFields = {},
  target = {},
  accountId = '',
  fetchAdobeFields = () => {},
  updateMTUs = () => {},
}) => {
  const [selectedMTUs, setSelectedMTUs] = useState({})

  const [validAdobeFields, setValidAdobeFields] = useState({})

  const assignedTargets = multiTargets.map(mt => `${mt.remote_realm_id} + ${mt.remote_target_id}`)

  useEffect(() => {
    // have to know our base configuration first.
    if (!adobeFields[target.remote_realm_id]) {
      return
    }
    const baseAdobeClassifications = adobeFields[target.remote_realm_id].targets.find(
      t => target.remote_target_id === t.id,
    )?.classifications_names
    const newOptions = Object.keys(adobeFields).reduce((memo, item) => {
      adobeFields[item].validTargets = checkForMatches(
        // no-no
        adobeFields[item].targets,
        baseAdobeClassifications,
        target.remote_target_id,
      )
        .filter(mtu => !assignedTargets.includes(`${item} + ${mtu.id}`))
        .filter(mtu => !(item === target.remote_realm_id && mtu.id === target.remote_target_id))
      memo[adobeFields[item].remoteRealmId] = adobeFields[item] // no-no
      return memo
    }, {})
    setValidAdobeFields(newOptions)
  }, [adobeFields])

  const onUpdate = () => {
    updateMTUs(selectedMTUs)
  }

  return (
    <div style={{ padding: 12, paddingTop: 24 }}>
      <div>
        {realms.map(realm => (
          <Accordion
            key={realm.remote_realm_id}
            onChange={(e, newVal) => {
              if (newVal && !adobeFields[realm.remote_realm_id]) {
                fetchAdobeFields(accountId, realm.remote_realm_id)
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography>{realm.realm_name}</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ flexDirection: 'column' }}>
              {validAdobeFields[realm.remote_realm_id] ? (
                validAdobeFields[realm.remote_realm_id].validTargets.map(tar => (
                  <div key={tar.id}>
                    <FormControlLabel
                      style={{ margin: 0 }}
                      control={
                        <Checkbox
                          checked={!!selectedMTUs[`${realm.remote_realm_id} + ${tar.id}`]}
                          onChange={(e, newVal) => {
                            setSelectedMTUs({
                              ...selectedMTUs,
                              [`${realm.remote_realm_id} + ${tar.id}`]: newVal
                                ? {
                                    ...tar,
                                    remote_realm_id: realm.remote_realm_id,
                                    remote_realm_name: realm.realm_name,
                                    remote_target_name: tar.name,
                                    status: true,
                                  }
                                : null,
                            })
                          }}
                          disabled={
                            !!assignedTargets.includes[`${realm.remote_realm_id} + ${tar.id}`]
                          }
                          value={!!selectedMTUs[`${realm.remote_realm_id} + ${tar.id}`]}
                          style={{ padding: 2 }}
                          color="secondary"
                        />
                      }
                      label={tar.name}
                    />
                  </div>
                ))
              ) : (
                <CircularProgress />
              )}
              {validAdobeFields[realm.remote_realm_id] &&
                !validAdobeFields[realm.remote_realm_id].validTargets.length && (
                  <div>{t`This report suite has no compatible reports available to add.`}</div>
                )}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <Button variant="contained" color="primary" onClick={onUpdate} style={{ width: '100%' }}>
          {t`Add multi targets`}
        </Button>
      </div>
    </div>
  )
}

EditMTU.propTypes = {
  multiTargets: array,
  realms: array,
  adobeFields: object,
  target: object,
  accountId: string,
  fetchAdobeFields: func,
  updateMTUs: func,
}
